import React, { type FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import reixLogo from 'images/reix_logo.png';

type LoginPromptModalProps = {
  visible: boolean;
  onHide: () => void;
  message?: string;
};

const DEFAULT_MESSAGE =
  'Create an account or log in to save properties and build lists.';

const buildAuthUrl = (path: string): string => {
  const returnTo = window.location.pathname + window.location.search;
  return `${path}?return_to=${encodeURIComponent(returnTo)}`;
};

const Z_INDEX = 100000;

const LoginPromptModal: FC<LoginPromptModalProps> = ({
  visible,
  onHide,
  message,
}) => {
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onHide();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, onHide]);

  if (!visible || typeof document === 'undefined') return null;

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-prompt-title"
      onClick={onHide}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: Z_INDEX,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          zIndex: Z_INDEX + 1,
          width: '28rem',
          maxWidth: '90vw',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <h2
            id="login-prompt-title"
            className="text-lg font-semibold text-gray-900"
          >
            Sign in to continue
          </h2>
          <img
            className="hidden h-10 w-auto lg:block"
            src={reixLogo}
            alt="REIX"
          />
        </div>
        <div className="px-5 py-4">
          <p className="text-gray-700 mb-6">{message || DEFAULT_MESSAGE}</p>
          <div className="grid grid-cols-2 gap-3">
            <a
              href={buildAuthUrl('/users/sign_in')}
              className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Log in
            </a>
            <a
              href={buildAuthUrl('/users/sign_up')}
              className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Sign up
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default LoginPromptModal;
